import { useEffect, useState } from 'react'

import { Option, Select } from '@components/Select'

import pdfIcon from '@assets/images/pdf-icon.svg'
import PlanLinkedIcon from '@assets/images/plan-linked-status-icon.svg?react'

import { ProjectFile } from '@types'

import { useProjectPlans } from '@hooks/project/project-plans.hook'

import { getFileUrl } from '@utils/getFileUrl'

import {
  FilePreview,
  FilePreviewContainer,
  ItemContainer,
  ItemLabel,
  SpanFilePreview,
} from './SelectPlanFromZone.styled'

interface Props {
  required?: boolean
  projectId: string
  projectFile?: ProjectFile
  label: string
  placeholder: string
  disabledSearch?: boolean
  disabled?: boolean
  onChangeProjectFile: (name: string, projectFile?: ProjectFile) => void
}

type ProjectFileWithUrl = ProjectFile & { pictureUrl?: string }

export const SelectPlanFromZone: React.FC<Props> = (props: Props) => {
  const {
    required,
    projectId,
    projectFile,
    disabledSearch = false,
    disabled = false,
    onChangeProjectFile,
    label,
    placeholder,
  } = props
  const { data } = useProjectPlans(projectId || '')
  const [search, setSearch] = useState<string>(projectFile?.name || '')

  const [documents, setDocuments] = useState<ProjectFileWithUrl[] | undefined>(data?.project?.plans)

  const loadDocumentPreview = async () => {
    const formattedFiles = await Promise.all(
      (data?.project?.plans || []).map(async (projectFile) => {
        const url = await getFileUrl(projectFile.url)
        return { ...projectFile, pictureUrl: url }
      }),
    )

    setDocuments(formattedFiles as any[])
  }

  useEffect(() => {
    if (data?.project?.plans) {
      setDocuments(data?.project?.plans)
      loadDocumentPreview()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.project?.plans])

  const options: Option[] = (documents || []).map((document) => ({
    id: document.id,
    label: document.name,
    data: document,
    Component: () => (
      <ItemContainer>
        <FilePreviewContainer>
          {document.file.type.indexOf('pdf') != -1 && <FilePreview src={pdfIcon} />}
          {document.file.type.indexOf('image') != -1 && <FilePreview src={document.pictureUrl} />}
          {document.zoneId && (
            <SpanFilePreview>
              <PlanLinkedIcon />
            </SpanFilePreview>
          )}
        </FilePreviewContainer>
        <ItemLabel>{document.name}</ItemLabel>
      </ItemContainer>
    ),
  }))

  const getDocumentUrl = (id: string) => options.find((doc) => doc.id === id)?.data.pictureUrl

  const selectedOption: Option | undefined = projectFile && {
    id: projectFile.id,
    label: projectFile.name,
    data: projectFile,
    Component: () => (
      <ItemContainer>
        <FilePreviewContainer>
          {projectFile.file.type.indexOf('pdf') != -1 && <FilePreview src={pdfIcon} />}
          {projectFile.file.type.indexOf('image') != -1 && <FilePreview src={getDocumentUrl(projectFile.id)} />}
          {projectFile.zoneId && (
            <SpanFilePreview>
              <PlanLinkedIcon />
            </SpanFilePreview>
          )}
        </FilePreviewContainer>
        {disabledSearch && <ItemLabel>{projectFile.name}</ItemLabel>}
      </ItemContainer>
    ),
  }

  const filteredOptions = options.filter(
    (option) => option.label.toLowerCase().indexOf((search || '').toLowerCase()) !== -1,
  )

  return (
    <Select
      disabledVerticalCorrection
      hideOptional
      required={required}
      label={label}
      disabled={disabled}
      placeholder={placeholder}
      selected={selectedOption}
      search={disabledSearch ? undefined : search}
      onSearch={disabledSearch ? undefined : setSearch}
      options={disabledSearch ? options : filteredOptions}
      onClick={(option) => {
        if (!option) {
          setSearch('')
          onChangeProjectFile('', undefined)
          return
        }
        if (option.id === selectedOption?.id) {
          setSearch('')
          onChangeProjectFile('', undefined)
        } else {
          setSearch(option.label)
          onChangeProjectFile(option.label, option.data as ProjectFile)
        }
      }}
    />
  )
}
